import { useEffect, useRef } from 'react';

// Keeps the newest chat turn (and any pending/typing indicator) in view.
// Shared by `HrMaeveChat`, `ManagerMaeveChat`'s `Thread`, and
// `EmployeeCompanionChat` — all three key the same effect off exactly two
// values (turn count, in-flight flag), previously hand-duplicated in each
// file. `historyLength`/`isPending` are the only things that change a
// transcript's height, so they're the whole dependency list: keystrokes in
// the composer must NOT re-trigger this, or the view fights a user scrolling
// back to re-read an earlier answer while typing.
//
// `skipWhenEmpty`: Companion's opening state has no greeting bubble and no
// transcript content until the first turn lands or a request is in flight —
// scrolling into view then would just jump to an empty container. HR (an
// `EmptyState` block) and Manager (an always-rendered opening greeting
// bubble) both have something to scroll to from the first render, so they
// don't set this.
export function useScrollToEnd(
  historyLength: number,
  isPending: boolean,
  options: { skipWhenEmpty?: boolean; smooth?: boolean } = {},
) {
  const { skipWhenEmpty = false, smooth = false } = options;
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (skipWhenEmpty && historyLength === 0 && !isPending) return;
    // `exactOptionalPropertyTypes` forbids `behavior: undefined` — omit the
    // key entirely rather than set it, so the DOM default (instant) applies.
    endRef.current?.scrollIntoView(
      smooth ? { block: 'end', behavior: 'smooth' } : { block: 'end' },
    );
  }, [historyLength, isPending, skipWhenEmpty, smooth]);

  return endRef;
}
